/**
 * Refresh every surface that shows the Ready-to-Enter population (2026-07-29).
 *
 * There are TWO server queries over the same rows: `readyToEnter` (flat) and
 * `readyToEnterWorkOrders` (grouped by work order). The page Dave actually
 * looks at renders the grouped one; the flat one is rendered nowhere.
 *
 * Three mutations on Work Order Detail — mark entered, undo entered, request
 * re-edit — invalidated only the flat query. So a manager opened a card from
 * the queue, marked it entered, went back, and the card was still sitting
 * there: the query behind the screen had never been told anything changed.
 * `staleTime` is 30s, so it corrected itself shortly after, which is worse than
 * a hard failure — it looks like the button did not work, and the natural
 * response is to press it again.
 *
 * Two names for one population is the trap; one call site is the way out.
 * Anything that changes whether a write-up is waiting for the ERP calls this,
 * and cannot forget half of it.
 */

interface Invalidatable {
  /** tRPC returns a Promise here; `unknown` covers it and keeps test stubs simple. */
  invalidate: () => unknown;
}

/** The slice of tRPC utils this touches — structural, so tests can pass a stub. */
export interface ManagerQueueUtils {
  manager: {
    readyToEnter: Invalidatable;
    readyToEnterWorkOrders: Invalidatable;
    readyToBill: Invalidatable;
    readyToBillOpen: Invalidatable;
    dashboardStats: Invalidatable;
    warrantyQueue: Invalidatable;
  };
}

/**
 * Invalidate both queue queries plus the home tiles that count the same rows.
 * The tiles are included deliberately: a write-up leaving `pushed` changes
 * "Ready to enter" too, and leaving that stale reproduces the original
 * complaint one screen up.
 */
export function invalidateManagerQueues(utils: ManagerQueueUtils): void {
  void utils.manager.readyToEnter.invalidate();
  void utils.manager.readyToEnterWorkOrders.invalidate();
  // Ready to bill shows each line's 3C state, so a 3C changing state changes it.
  void utils.manager.readyToBill.invalidate();
  // Its "Not closed yet" section lists pushed 3Cs on open orders — same rows.
  void utils.manager.readyToBillOpen.invalidate();
  void utils.manager.dashboardStats.invalidate();
  // The warranty queue is the same population narrowed to flagged write-ups,
  // and it was left out of this helper — so marking a warranty-flagged 3C
  // entered cleared it from Ready-to-Enter and left it sitting in Flags. The
  // identical defect this helper was written to fix, one queue over.
  void utils.manager.warrantyQueue.invalidate();
}
